const HTTPS_PREFIX = 'https://';

export const WEBHOOK_EVENT_CATEGORIES = ['PAY_WITH_CRYPTO', 'MERCHANTS', 'STABLECOIN_SETTLEMENT', 'CRYPTO_PAYOUT'] as const;

export type WebhookDestinationRef = {
  uuid: string;
  destination: string;
  name?: string | null;
  enabled?: boolean;
  eventCategories?: readonly string[];
};

export type WebhookWrite = {
  destination: string;
  eventCategories: readonly string[];
  name?: string;
  enabled?: boolean;
};

export type WebhookSyncPlan =
  | { type: 'none' }
  | { type: 'delete'; uuid: string }
  | ({ type: 'create' } & WebhookWrite)
  | ({ type: 'replace'; uuid: string } & WebhookWrite);

export const toDestinationUrl = (fieldValue?: string | null) => {
  const trimmed = (fieldValue ?? '').trim();
  if (!trimmed) {
    return '';
  }

  return trimmed.startsWith(HTTPS_PREFIX) ? trimmed : `${HTTPS_PREFIX}${trimmed}`;
};

export const toWebhookFieldValue = (destination?: string | null) => (destination ?? '').replace(HTTPS_PREFIX, '');

const destinationForUrl = (destinations: WebhookDestinationRef[], fieldValue?: string | null) => {
  const url = toDestinationUrl(fieldValue);
  if (!url) {
    return undefined;
  }

  return destinations.find((row) => row.destination === url);
};

export const planWebhookSync = ({
  destinations,
  previousField,
  nextField,
}: {
  destinations: WebhookDestinationRef[];
  previousField?: string | null;
  nextField?: string | null;
}): WebhookSyncPlan => {
  const current = destinationForUrl(destinations, previousField);
  const nextUrl = toDestinationUrl(nextField);
  const existingNext = destinationForUrl(destinations, nextField);

  if (nextUrl === (current?.destination ?? '')) {
    return { type: 'none' };
  }

  if (!nextUrl) {
    return current ? { type: 'delete', uuid: current.uuid } : { type: 'none' };
  }

  if (existingNext) {
    return current && current.uuid !== existingNext.uuid ? { type: 'delete', uuid: current.uuid } : { type: 'none' };
  }

  if (!current) {
    return { type: 'create', destination: nextUrl, eventCategories: WEBHOOK_EVENT_CATEGORIES };
  }

  const keptName = current.name && current.name !== current.destination ? current.name : undefined;

  return {
    type: 'replace',
    uuid: current.uuid,
    destination: nextUrl,
    eventCategories: current.eventCategories?.length ? current.eventCategories : WEBHOOK_EVENT_CATEGORIES,
    ...(keptName ? { name: keptName } : {}),
    ...(current.enabled !== undefined ? { enabled: current.enabled } : {}),
  };
};

export const webhookFieldFromDestinations = (destinations: WebhookDestinationRef[]) => {
  const [destination] = destinations;
  return destination ? toWebhookFieldValue(destination.destination) : undefined;
};
